import { differenceInCalendarDays, differenceInSeconds, format } from 'date-fns';
import PropTypes from 'prop-types';
import React, { useEffect, useState, useMemo, createRef } from 'react';
import { useTranslation } from 'react-i18next';
import useClassNames from '@medflex/hooks/useClassNames';
import { CHAT_MESSAGE_TYPE, SESSION_TYPES } from '@medflex/utils/constants';
import { MessageTypes } from '@medflex/shared-types';
import { gdtConstants } from '@medflex/gdt';
import { AutoResponseMessage } from '@medflex/shared-constants';
import {
  CALL_INFO_CENTER_URLS,
  PRECALL_TEST_URL,
  SECONDS_GROUP_CONSECUTIVE_MESSAGES,
} from '../../constants';
import SessionMessage from '../SessionMessage';
import ChatMessage from '../ChatMessage';
import ChatMessageHeader from '../ChatMessageHeader';
import ChatMessageStreamSeparator from '../ChatMessageStreamSeparator';
import { IconFlag } from '../Icons';
import {
  InquiryDetails,
  InquiryHeader,
  InquirySourceBadge,
  PamInquiryCard,
  PhoneStatusBadge,
} from '../PamInquiry';
import SupportWelcomeMessage from '../SupportWelcomeMessage';
import MainRepresentative from '../MainRepresentative';
import RepresentedPerson from '../RepresentedPerson';

const willRenderMessageHeader = (prevMessage, message) => {
  const diffSeconds = () => differenceInSeconds(message.time, prevMessage.time);
  return (
    !prevMessage ||
    (prevMessage.type && prevMessage.type === 'multicast') ||
    (message.type && message.type === 'multicast') ||
    (message.type && message.type === 'call') ||
    (prevMessage.type && prevMessage.type === 'call') ||
    prevMessage.sender.id !== message.sender.id ||
    diffSeconds() > SECONDS_GROUP_CONSECUTIVE_MESSAGES
  );
};

const isPatient = id => id && id.startsWith('pat-');

const DeliveryConfirmation = ({ autoResponseMessage }) => {
  return (
    <div className="flex">
      <div className="border border-slate-200 p-3 ml-8 mt-3 rounded-lg text-slate-600 whitespace-pre-line max-w-line-length">
        {autoResponseMessage || AutoResponseMessage.DEFAULT}
      </div>
    </div>
  );
};

DeliveryConfirmation.propTypes = {
  autoResponseMessage: PropTypes.string,
};

DeliveryConfirmation.defaultProps = {
  autoResponseMessage: null,
};

const MessageAlignmentWrapper = ({ children, message }) => {
  const chatMessageCn = useClassNames(() => [
    'relative',
    'flex',
    'gap-2',
    ...(message.isSender || message.isInSenderPractice ? ['flex-row-reverse', 'pr-6'] : ['pl-6']),
  ]);

  return <div className={chatMessageCn}>{children}</div>;
};

MessageAlignmentWrapper.propTypes = {
  children: PropTypes.node.isRequired,
  message: PropTypes.shape({
    id: PropTypes.string.isRequired,
    time: PropTypes.string.isRequired,
    isSender: PropTypes.bool,
    type: PropTypes.string,
    user: PropTypes.shape({
      avatar: PropTypes.string,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      fullNameWithTitle: PropTypes.string.isRequired,
      initials: PropTypes.string,
    }),
    text: PropTypes.string,
    file: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      mediaType: PropTypes.string,
      bytes: PropTypes.number,
    }),
    isInSenderPractice: PropTypes.bool,
  }).isRequired,
};

const SystemMessage = ({ message }) => {
  const { t } = useTranslation('global');
  const { participantAdded, participantRemoved, hasLeft } = message;

  return (
    <div className="m-4 mt-5 ml-8 pl-3">
      <div className="flex items-center">
        <IconFlag className="text-slate-400 mr-3" />
        <div className="text-slate-600">
          {participantAdded &&
            t('ChatMessageStream.message.system.added', {
              name: participantAdded,
              interpolation: { escapeValue: true },
            })}
          {participantRemoved &&
            !hasLeft &&
            t('ChatMessageStream.message.system.removed', {
              name: participantRemoved,
              interpolation: { escapeValue: true },
            })}
          {participantRemoved &&
            hasLeft &&
            t('ChatMessageStream.message.system.left', {
              name: participantRemoved,
              interpolation: { escapeValue: true },
            })}
        </div>
      </div>
    </div>
  );
};

SystemMessage.propTypes = {
  message: PropTypes.object,
};

const ChatMessageStream = ({
  messages,
  newMessagesLabel,
  messageTypeLabels = {},
  lastReadMessageId,
  fileMessageClickHandler,
  fileMessageDownloadHandler,
  audioSourceResolver,
  MessageActionMenu,
  PatientMessageAction,
  deletedMessageText,
  contactName,
  onSessionParticipate,
  anchor,
  userName,
  className,
  messageSeenText,
  onScheduleDemo,
  isUserPatient,
  autoResponseMessage,
  isBasicUser,
  onWriteExamination,
  ...props
}) => {
  const cn = useClassNames(
    () => [className, 'select-none', 'lg:select-auto', 'webtest-chat'],
    [className],
  );
  const lastMessage = messages.slice(-1).shift();
  const { t } = useTranslation('global');
  const [isScrolled, setIsScrolled] = useState(false);

  const messageRefs = useMemo(() => {
    if (messages && anchor) {
      return messages.reduce((acc, cur) => ({ ...acc, [cur.id]: createRef() }), {});
    }
    return {};
  }, [messages, anchor]);

  const lastReadMessageIndex = useMemo(() => {
    const chatIndexes = messages
      .map((msg, index) => ({ ...msg, index }))
      .filter(
        ({ type }) => ![CHAT_MESSAGE_TYPE.SYSTEM, CHAT_MESSAGE_TYPE.PAM_REQUEST].includes(type),
      );

    if (!lastReadMessageId) {
      return messages.findIndex(
        ({ type }) => ![CHAT_MESSAGE_TYPE.SYSTEM, CHAT_MESSAGE_TYPE.PAM_REQUEST].includes(type),
      );
    }
    if (chatIndexes.length > 0) {
      const lastReadMessage = chatIndexes.findIndex(({ id }) => id === lastReadMessageId);
      if (
        lastReadMessage === -1 ||
        chatIndexes[lastReadMessage].index === chatIndexes[chatIndexes.length - 1].index
      )
        return null;

      return chatIndexes[lastReadMessage + 1].index;
    }
    return null;
  }, [messages, lastReadMessageId]);

  useEffect(() => {
    if (anchor && messageRefs[anchor] && !isScrolled) {
      setIsScrolled(true);
      if (messageRefs[anchor].current)
        messageRefs[anchor].current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [anchor, isScrolled, messageRefs]);

  return (
    <article data-component="ChatMessageStream" className={cn} {...props}>
      {messages.map((message, index, arr) => {
        const prevMessage = index > 0 ? arr[index - 1] : undefined;
        const renderDateSeparator =
          index === 0 || differenceInCalendarDays(message.time, prevMessage.time) > 0;
        const isSession =
          message.type &&
          [CHAT_MESSAGE_TYPE.SESSION_CALL, CHAT_MESSAGE_TYPE.SESSION_MEETING].includes(
            message.type,
          );
        const isPamRequest = message.type && [CHAT_MESSAGE_TYPE.PAM_REQUEST].includes(message.type);

        const isSystemMessage = message.type === CHAT_MESSAGE_TYPE.SYSTEM;
        const isWelcomeMessage = message.type === CHAT_MESSAGE_TYPE.SUPPORT_WELCOME_MESSAGE;
        const isMessage = !isSession && !isWelcomeMessage && !isSystemMessage && !isPamRequest;
        const renderUnreadMarker = lastReadMessageIndex === index;
        const renderMessageHeader =
          renderDateSeparator || willRenderMessageHeader(prevMessage, message);
        const isMulticast = message.type && message.type === 'multicast';

        return (
          <React.Fragment key={message.id}>
            {(renderDateSeparator || renderUnreadMarker) && (
              <ChatMessageStreamSeparator
                date={renderDateSeparator ? message.time : undefined}
                info={renderUnreadMarker ? newMessagesLabel : undefined}
                className={[renderUnreadMarker && 'text-red-600 border-red-600']}
              />
            )}
            {!isSystemMessage && renderMessageHeader && (
              <ChatMessageHeader
                time={message.time}
                avatar={message.user.avatar}
                initials={message.user.initials}
                name={`${isMulticast ? t('ChatMessageStream.message.multicast') : ''}${
                  message.user.fullNameWithTitle
                }${message.user.dateOfBirth ? `, * ${format(message.user.dateOfBirth, 'DD.MM.YYYY')}` : ''}`}
                info={message.user.jobTitle}
                isSender={message.isSender}
                isInSenderPractice={message.isInSenderPractice}
              />
            )}
            {isWelcomeMessage && (
              <SupportWelcomeMessage
                name={userName}
                onScheduleDemo={onScheduleDemo}
                isBasicUser={isBasicUser}
              />
            )}
            {isMessage && (
              <div id={message.id} ref={messageRefs[message.id]}>
                <ChatMessage
                  message={message}
                  messageTypeLabels={messageTypeLabels}
                  MessageActionMenu={MessageActionMenu}
                  messageSeenText={messageSeenText}
                  PatientMessageAction={PatientMessageAction}
                  deletedMessageText={deletedMessageText}
                  fileMessageClickHandler={fileMessageClickHandler}
                  audioSourceResolver={() => audioSourceResolver(message)}
                  isUserPatient={isUserPatient}
                />
              </div>
            )}
            {isSystemMessage && <SystemMessage message={message} />}
            {isSession && (
              <MessageAlignmentWrapper message={message}>
                <SessionMessage
                  {...message}
                  onParticipate={onSessionParticipate}
                  infoArticleUrl={
                    message.isPatient
                      ? CALL_INFO_CENTER_URLS.patient.howTo
                      : CALL_INFO_CENTER_URLS.physician.howTo
                  }
                  preCallTestUrl={PRECALL_TEST_URL}
                />
              </MessageAlignmentWrapper>
            )}
            {isPamRequest && (
              <MessageAlignmentWrapper message={message}>
                <PamInquiryCard
                  className="max-w-lg w-full"
                  header={
                    <InquiryHeader
                      isPatient={message.isPatient}
                      inquiry={message.data}
                      link={!message.isPatient ? `/inbox/requests/${message.id}` : ''}
                    />
                  }
                >
                  <div className="space-y-3 mb-3">
                    {!message.isPatient && (
                      <InquirySourceBadge requestSource={message.data.source} />
                    )}
                    {message.data.phoneStatus && (
                      <PhoneStatusBadge phoneStatus={message.data.phoneStatus} />
                    )}
                    {message.data.contactDetails?.representedPerson?.userId && (
                      <MainRepresentative userDetails={message.data.contactDetails} />
                    )}
                    {message.data.contactDetails?.representedPerson &&
                      !message.data.contactDetails?.representedPerson.userId && (
                        <RepresentedPerson
                          userDetails={message.data.contactDetails?.representedPerson}
                        />
                      )}
                  </div>
                  <div className="divide-y border-t space-y-3 mb-3">
                    <InquiryDetails
                      inquiry={message.data}
                      onOpenFile={file => fileMessageClickHandler(file, MessageTypes.PAM)}
                      onFetchAudioSource={file =>
                        audioSourceResolver({ files: [file] }, MessageTypes.PAM)
                      }
                      onDownloadFile={fileMessageDownloadHandler}
                      onWriteExamination={
                        onWriteExamination
                          ? file =>
                              onWriteExamination({
                                files: [file],
                                fileSource: gdtConstants.FileSources.PAM,
                              })
                          : undefined
                      }
                      isPatient={message.isPatient}
                      showFileActions
                    />
                  </div>
                </PamInquiryCard>
              </MessageAlignmentWrapper>
            )}
          </React.Fragment>
        );
      })}
      {lastMessage &&
        ((isPatient(lastMessage.sender.id) && lastMessage.sender.meOrSurrogate) ||
          (lastMessage.sessionType === SESSION_TYPES.PAM_CHAT &&
            isPatient(lastMessage.user.userId) &&
            lastMessage.isSender)) && (
          <DeliveryConfirmation autoResponseMessage={autoResponseMessage} />
        )}
      <div className="flex-grow" />
    </article>
  );
};

ChatMessageStream.propTypes = {
  messages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      time: PropTypes.string.isRequired,
      isSender: PropTypes.bool,
      type: PropTypes.string,
      user: PropTypes.shape({
        avatar: PropTypes.string,
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        fullNameWithTitle: PropTypes.string.isRequired,
        initials: PropTypes.string,
      }),
      text: PropTypes.string,
      file: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        mediaType: PropTypes.string,
        bytes: PropTypes.number,
      }),
    }),
  ),
  newMessagesLabel: PropTypes.string,
  messageTypeLabels: PropTypes.objectOf(PropTypes.string),
  lastReadMessageId: PropTypes.string,
  fileMessageClickHandler: PropTypes.func,
  fileMessageDownloadHandler: PropTypes.func,
  audioSourceResolver: PropTypes.func,
  PatientMessageAction: PropTypes.func,
  MessageActionMenu: PropTypes.func,
  deletedMessageText: PropTypes.string,
  contactName: PropTypes.string,
  onSessionParticipate: PropTypes.func,
  onScheduleDemo: PropTypes.func,
  userName: PropTypes.string,
  anchor: PropTypes.string,
  messageSeenText: PropTypes.string,
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func,
    PropTypes.array,
    PropTypes.object,
  ]),
  isUserPatient: PropTypes.bool,
  autoResponseMessage: PropTypes.string,
  isBasicUser: PropTypes.bool.isRequired,
  onWriteExamination: PropTypes.func,
};

ChatMessageStream.defaultProps = {
  messages: [],
  onSessionParticipate: () => {},
  onScheduleDemo: () => {},
  audioSourceResolver: () => {},
  autoResponseMessage: null,
  isUserPatient: false,
  onWriteExamination: null,
};

export default ChatMessageStream;
